/**
 * SEO Utilities for CodiceCivile.AI
 * Handles meta tags, Open Graph, Twitter Cards, and structured data
 */

export interface SEOConfig {
  title: string;
  description: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

/** Canonical site origin — every canonical/og:url/og:image must use it. */
export const SITE_ORIGIN = 'https://codicecivile.ai';

const TITLE_SUFFIX = ' | CodiceCivile.AI';

/**
 * Normalize any path or URL to its canonical form:
 * apex origin + pathname, no query string, no hash, no trailing slash
 * (except the root path).
 */
export function canonicalUrl(pathOrUrl: string): string {
  let pathname = pathOrUrl;
  if (/^https?:\/\//i.test(pathOrUrl)) {
    try {
      pathname = new URL(pathOrUrl).pathname;
    } catch {
      pathname = '/';
    }
  }
  pathname = pathname.split('?')[0].split('#')[0];
  if (!pathname.startsWith('/')) {
    pathname = '/' + pathname;
  }
  if (pathname.length > 1 && pathname.endsWith('/')) {
    pathname = pathname.replace(/\/+$/, '');
  }
  return pathname === '/' ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${pathname}`;
}

/** Make a relative asset path absolute on the site origin. */
export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }
  return `${SITE_ORIGIN}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
}

/** Append the brand suffix exactly once, wherever the input came from. */
function withBrandSuffix(title: string): string {
  const stripped = title.replace(/\s*\|\s*CodiceCivile\.AI\s*$/i, '').trim();
  return `${stripped}${TITLE_SUFFIX}`;
}

/**
 * Update document meta tags for SEO
 */
export function updateMetaTags(config: SEOConfig) {
  const {
    title,
    description,
    keywords = [],
    image = '/og-image.png',
    url = window.location.pathname,
    type = 'website',
    author,
    publishedTime,
    modifiedTime,
  } = config;

  const pageUrl = canonicalUrl(url);
  const imageUrl = absoluteUrl(image);

  // Update title
  document.title = withBrandSuffix(title);

  // Update or create meta tags
  updateOrCreateMetaTag('description', description);

  if (keywords.length > 0) {
    updateOrCreateMetaTag('keywords', keywords.join(', '));
  }

  // Open Graph tags
  updateOrCreateMetaTag('og:title', title, 'property');
  updateOrCreateMetaTag('og:description', description, 'property');
  updateOrCreateMetaTag('og:image', imageUrl, 'property');
  updateOrCreateMetaTag('og:url', pageUrl, 'property');
  updateOrCreateMetaTag('og:type', type, 'property');
  updateOrCreateMetaTag('og:site_name', 'CodiceCivile.AI', 'property');
  updateOrCreateMetaTag('og:locale', 'it_IT', 'property');

  // Twitter Card tags (name attribute — index.html static tags must match)
  updateOrCreateMetaTag('twitter:card', 'summary_large_image');
  updateOrCreateMetaTag('twitter:title', title);
  updateOrCreateMetaTag('twitter:description', description);
  updateOrCreateMetaTag('twitter:image', imageUrl);

  // Additional meta tags
  if (author) {
    updateOrCreateMetaTag('author', author);
  }

  if (publishedTime) {
    updateOrCreateMetaTag('article:published_time', publishedTime, 'property');
  }

  if (modifiedTime) {
    updateOrCreateMetaTag('article:modified_time', modifiedTime, 'property');
  }

  // Canonical URL
  updateOrCreateLink('canonical', pageUrl);
}

/**
 * Helper function to update or create meta tag
 */
function updateOrCreateMetaTag(
  name: string,
  content: string,
  attribute: 'name' | 'property' = 'name'
) {
  let element = document.querySelector(`meta[${attribute}="${name}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
}

/**
 * Helper function to update or create link tag
 */
function updateOrCreateLink(rel: string, href: string) {
  let element = document.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
}

export type StructuredDataType =
  | 'LegalService'
  | 'Organization'
  | 'FAQPage'
  | 'WebSite'
  | 'SoftwareApplication'
  | 'BreadcrumbList';

/**
 * Generate JSON-LD structured data
 */
export function generateStructuredData(type: StructuredDataType, data: any): string {
  const baseContext = {
    '@context': 'https://schema.org',
  };

  let structuredData: any = { ...baseContext };

  switch (type) {
    case 'LegalService':
      structuredData = {
        ...structuredData,
        '@type': 'LegalService',
        name: 'CodiceCivile.AI',
        description: 'Ricerca legale con intelligenza artificiale per il Codice Civile italiano. Accesso diretto al database normattiva.it con AI.',
        url: 'https://codicecivile.ai',
        logo: 'https://codicecivile.ai/icon-512.png',
        areaServed: {
          '@type': 'Country',
          name: 'Italy',
        },
        serviceType: 'Legal Research & AI Assistance',
        priceRange: 'Free - €99',
        provider: {
          '@type': 'Organization',
          name: 'CodiceCivile.AI',
          url: 'https://codicecivile.ai',
        },
        ...data,
      };
      break;

    case 'Organization':
      structuredData = {
        ...structuredData,
        '@type': 'Organization',
        name: 'CodiceCivile.AI',
        url: 'https://codicecivile.ai',
        logo: 'https://codicecivile.ai/icon-512.png',
        description: 'Piattaforma di ricerca legale con intelligenza artificiale per il diritto italiano',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'Customer Support',
          areaServed: 'IT',
          availableLanguage: ['Italian', 'English'],
          email: 'support@codicecivile.ai',
        },
        ...data,
      };
      break;

    case 'FAQPage':
      structuredData = {
        ...structuredData,
        '@type': 'FAQPage',
        mainEntity: data.questions.map((q: { question: string; answer: string }) => ({
          '@type': 'Question',
          name: q.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: q.answer,
          },
        })),
      };
      break;

    case 'WebSite':
      structuredData = {
        ...structuredData,
        '@type': 'WebSite',
        name: 'CodiceCivile.AI',
        url: `${SITE_ORIGIN}/`,
        inLanguage: 'it-IT',
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${SITE_ORIGIN}/massime?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
        ...data,
      };
      break;

    case 'SoftwareApplication':
      structuredData = {
        ...structuredData,
        '@type': 'SoftwareApplication',
        name: 'CodiceCivile.AI',
        url: `${SITE_ORIGIN}/`,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        description:
          'Ancoraggio legale italiano per qualsiasi LLM: 39 codici, massime di Cassazione con sentenza originale, MCP aperto, redazione PII automatica. Piattaforma agentica normattivaai.',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'EUR',
          description: 'Piano gratuito disponibile',
        },
        ...data,
      };
      break;

    case 'BreadcrumbList':
      structuredData = {
        ...structuredData,
        '@type': 'BreadcrumbList',
        itemListElement: (data.items || []).map(
          (item: { name: string; url: string }, index: number) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: absoluteUrl(item.url),
          })
        ),
      };
      break;
  }

  return JSON.stringify(structuredData);
}

/**
 * Insert structured data into the page, keyed by id so multiple schema
 * blocks (e.g. Organization + WebSite + FAQPage) can coexist. Re-inserting
 * with the same id replaces that block only. The id is optional for
 * back-compatibility with single-block callers.
 */
export function insertStructuredData(jsonLd: string, id: string = 'default') {
  const existing = document.querySelector(
    `script[type="application/ld+json"][data-seo-id="${id}"]`
  );
  if (existing) {
    if (existing.textContent === jsonLd) {
      return; // no change — avoid pointless DOM churn
    }
    existing.remove();
  }

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-seo-id', id);
  script.text = jsonLd;
  document.head.appendChild(script);
}

/**
 * Predefined SEO configurations for common pages
 */
export const SEO_CONFIGS = {
  home: {
    title: 'Ricerca Legale AI | 39 Codici Italiani e Massime di Cassazione',
    description: 'Ancoraggio legale italiano per qualsiasi LLM: 39 codici, massime di Cassazione con sentenza, MCP aperto, redazione PII. Prova gratis.',
    keywords: [
      'codice civile italiano',
      'ricerca legale',
      'intelligenza artificiale diritto',
      'ai legale',
      'normattiva',
      'legislazione italiana',
      'ricerca giuridica',
      'mcp',
      'model context protocol',
      'claude desktop mcp',
      'grounding llm italiano',
      'massime cassazione sentenza',
      'pii redaction legale',
      'agentic legal tech',
      '39 codici',
    ],
  },
  pricing: {
    title: 'Prezzi e Piani | CodiceCivile.AI',
    description: 'Piani flessibili per avvocati, studenti e professionisti legali. Piano gratuito disponibile. Accesso completo al Codice Civile con AI.',
    keywords: ['prezzi ricerca legale', 'abbonamento codice civile', 'piano gratuito', 'legal tech prezzi'],
  },
  chat: {
    title: 'Chat AI Legale | Assistente Codice Civile',
    description: 'Fai domande sul diritto italiano e ricevi risposte immediate con citazioni da fonti ufficiali. AI specializzata in diritto civile.',
    keywords: ['chat ai legale', 'assistente legale', 'domande diritto italiano', 'chatbot legale'],
  },
  about: {
    title: 'Chi Siamo | CodiceCivile.AI',
    description: 'Scopri come CodiceCivile.AI sta rivoluzionando la ricerca legale in Italia con intelligenza artificiale e tecnologia avanzata.',
    keywords: ['legal tech italia', 'innovazione legale', 'ai diritto', 'ricerca giuridica moderna'],
  },
  faq: {
    title: 'Domande Frequenti | CodiceCivile.AI',
    description: 'Risposte alle domande più comuni su CodiceCivile.AI, ricerca legale con AI, prezzi, privacy e funzionalità.',
    keywords: ['faq ricerca legale', 'domande codice civile', 'aiuto legale online', 'come funziona'],
  },
  solutions: {
    title: 'Soluzioni per Professionisti | CodiceCivile.AI',
    description: 'Strumenti di ricerca legale per avvocati, studenti, notai e professionisti. Soluzioni personalizzate per ogni esigenza.',
    keywords: ['strumenti avvocati', 'ricerca legale professionisti', 'software studio legale', 'legal tech'],
  },
  howItWorks: {
    title: 'Come Funziona | CodiceCivile.AI',
    description: 'Scopri come utilizzare CodiceCivile.AI per la ricerca legale. Guida completa alle funzionalità AI e integrazione normattiva.it.',
    keywords: ['come funziona ai legale', 'guida ricerca legale', 'tutorial codice civile', 'usare ai diritto'],
  },
  privacy: {
    title: 'Informativa sulla Privacy',
    description: 'Informativa sulla privacy di CodiceCivile.AI: come trattiamo i tuoi dati personali nel rispetto del GDPR.',
    keywords: ['privacy policy', 'gdpr', 'trattamento dati', 'codicecivile.ai privacy'],
  },
  terms: {
    title: 'Termini di Servizio',
    description: 'Termini e condizioni di utilizzo della piattaforma di ricerca legale CodiceCivile.AI.',
    keywords: ['termini di servizio', 'condizioni uso', 'contratto utente'],
  },
  login: {
    title: 'Accedi',
    description: 'Accedi al tuo account CodiceCivile.AI per la ricerca legale con intelligenza artificiale sul diritto italiano.',
    keywords: ['accesso', 'login codicecivile', 'area riservata'],
  },
  register: {
    title: 'Registrati Gratis',
    description: 'Crea un account gratuito su CodiceCivile.AI: ricerca nel Codice Civile con AI, massime di Cassazione e citazioni verificate.',
    keywords: ['registrazione gratuita', 'account gratuito', 'prova gratis ricerca legale'],
  },
  invite: {
    title: 'Invito',
    description: 'Sei stato invitato su CodiceCivile.AI: registrati e ottieni vantaggi con il programma referral.',
    keywords: ['invito', 'referral', 'codice invito'],
  },
  marketingInvite: {
    title: 'Invito Esclusivo',
    description: 'Accetta il tuo invito esclusivo a CodiceCivile.AI, la ricerca legale italiana potenziata dall\'intelligenza artificiale.',
    keywords: ['invito esclusivo', 'accesso anticipato', 'legal tech italia'],
  },
  piattaforma: {
    title: 'Piattaforma agentica | normattivaai',
    description: "MCP, client locale per Claude Desktop, 39 codici italiani, massime di Cassazione, redazione PII. Tutto cio che serve per ancorare un LLM al diritto italiano.",
    keywords: [
      'mcp server legal',
      'claude desktop legal italy',
      'pii redaction',
      '39 codici',
      'massime cassazione',
      'agentic legal platform',
    ],
  },
  massime: {
    title: 'Massime di Cassazione - Ricerca Gratuita',
    description: 'Cerca tra migliaia di massime della Corte di Cassazione con AI. Ricerca semantica, riferimenti normativi e testo integrale.',
    keywords: ['massime cassazione', 'giurisprudenza cassazione', 'ricerca massime gratis', 'sentenze cassazione'],
  },
  guide: { title: 'Guida pratica al diritto italiano | CodiceCivile.AI', description: "Come funziona una sentenza, cos'è una massima, quando ti serve un avvocato. Le basi per capire il diritto italiano.", keywords: ['guida diritto italiano', 'sentenza cassazione', 'massima significato', 'quando serve avvocato'] },
  guideLeggereSentenza: { title: 'Come si legge una sentenza di Cassazione | CodiceCivile.AI', description: 'Le 4 parti di una decisione di Cassazione: intestazione, svolgimento, motivazione, dispositivo. Con un esempio reale.', keywords: ['sentenza cassazione struttura', 'motivazione sentenza', 'dispositivo significato'] },
  guideMassimaSentenza: { title: "Massima vs sentenza: che differenza c'è | CodiceCivile.AI", description: 'Massima, sentenza, principio di diritto: tre parole che si sentono spesso. Ecco cosa significano davvero e come si collegano.', keywords: ['differenza tra massima e sentenza', 'principio di diritto', 'massima cassazione significato'] },
  guideQuandoServeAvvocato: { title: "Quando ti serve un avvocato (e quando no) | CodiceCivile.AI", description: "Un'onesta guida ai limiti di un tool automatico. Quando consultare un professionista, quando basta informarsi.", keywords: ['quando serve avvocato', 'consulenza legale online', 'limiti ai legali'] },
  guideCassazione: { title: 'Cassazione: come funziona | CodiceCivile.AI', description: "Cos'è la Corte Suprema italiana, quando ci si va, e perché le sue decisioni fanno legge.", keywords: ['cassazione funzionamento', 'ricorso cassazione', 'sezioni unite'] },
  guideUsareChatgpt: {
    title: 'Usare CodiceCivile.AI con ChatGPT: tutorial MCP completo | CodiceCivile.AI',
    description: 'Collega ChatGPT ai 39 codici italiani e alle massime di Cassazione in 5 minuti. 3 metodi: Docker MCP Toolkit, Developer Mode custom URL, server stdio locale. Citazioni verificate.',
    keywords: [
      'chatgpt mcp',
      'chatgpt model context protocol',
      'chatgpt connector',
      'docker mcp toolkit',
      'chatgpt developer mode',
      'mcp server legal',
      'codice civile chatgpt',
      'cassazione chatgpt',
      'chatgpt citazioni verificate',
      'chatgpt diritto italiano',
    ],
  },
  guideLeggeIAItalia2025: {
    title: 'Legge 132/2025 Italia: obblighi AI per avvocati e professionisti | CodiceCivile.AI',
    description: "Cosa cambia con la Legge 132/2025 sull'intelligenza artificiale in Italia. Art. 12 disclosure per avvocati, commercialisti, consulenti. Sanzioni e conformità.",
    keywords: [
      'legge 132/2025',
      'legge intelligenza artificiale italia',
      'legge ia italia 2025',
      'obbligo informativa avvocati ia',
      'articolo 12 legge 132',
      'disclosure ai avvocato',
      'ai legge professionisti italia',
      'regolamentazione ia italia',
      'codice deontologico forense ia',
    ],
  },
  guideChatgptMCPItaliano: {
    title: 'ChatGPT MCP in italiano: setup con diritto italiano | CodiceCivile.AI',
    description: 'Configura ChatGPT con il Model Context Protocol per interrogare i 39 codici italiani e le massime di Cassazione. Setup in 5 minuti senza codice.',
    keywords: [
      'chatgpt mcp italiano',
      'chatgpt diritto italiano',
      'mcp server italiano',
      'chatgpt codice civile',
      'setup chatgpt mcp',
    ],
  },
  guideArt2043: {
    title: "Art. 2043 c.c. e responsabilità aquiliana: guida completa | CodiceCivile.AI",
    description: "L'art. 2043 c.c. è il pilastro della responsabilità civile italiana. Cosa dice, quando si applica, come si prova. Con Cassazione 2025-2026 e FAQ.",
    keywords: [
      'art 2043 codice civile',
      'responsabilità aquiliana',
      'responsabilità civile italia',
      'risarcimento danni',
      'danno ingiusto',
      'nesso causale più probabile che non',
      'onere della prova art 2043',
      'colpa aquiliana',
      'dolo aquiliano',
      'risarcimento danno non patrimoniale',
      'prescrizione risarcimento',
    ],
  },
  guideLicenziamento: {
    title: 'Licenziamento per giusta causa vs giustificato motivo: guida pratica | CodiceCivile.AI',
    description: "Quando il licenziamento è legittimo, quando no, quali tutele hai. Le tre figure di licenziamento (art. 3 L. 604/66), repechage, Jobs Act. Cass. 2025.",
    keywords: [
      'licenziamento giusta causa',
      'licenziamento giustificato motivo soggettivo',
      'licenziamento giustificato motivo oggettivo',
      'repechage obbligo',
      'tutele licenziamento jobs act',
      'impugnazione licenziamento 60 giorni',
      'reintegra posto di lavoro',
      'indennità licenziamento illegittimo',
      'licenziamento durante malattia',
      'conversione licenziamento',
      'art 18 statuto lavoratori',
      'licenziamento ritorsivo',
    ],
  },
  developers: { title: 'Per chi sviluppa | CodiceCivile.AI', description: "MCP, REST API, CLI. Tutta l'infrastruttura legale italiana, raggiungibile dal tuo codice.", keywords: ['mcp server', 'api legale italiana', 'cli legal tech', 'claude desktop mcp'] },
  developersMcp: { title: 'MCP Integration Guide | CodiceCivile.AI', description: 'Model Context Protocol server: endpoints, auth, code samples in cURL, Python, Node. Connect to Claude Desktop, Cursor, or your agent.', keywords: ['mcp integration', 'mcp server', 'claude desktop mcp', 'cursor mcp', 'api key'] },
  developersApi: { title: 'REST API Reference | CodiceCivile.AI', description: 'Plain HTTP access to the same data. Quick reference for the top endpoints with full request/response examples.', keywords: ['rest api legal', 'italian law api', 'codice civile api', 'massime api'] },
  developersCli: { title: 'CLI Tools | CodiceCivile.AI', description: 'The local stdio server, Python utility scripts, npm scripts, alembic, celery. Run the legal infrastructure from your terminal.', keywords: ['cli legal tech', 'python mcp stdio', 'npm scripts', 'alembic migrations', 'celery tasks'] },
  developersFaq: { title: 'Developer FAQ | CodiceCivile.AI', description: 'MCP vs REST, rate limits, streaming, PII, caching, self-hosting. The 8 questions developers ask us most.', keywords: ['mcp vs rest', 'api rate limit', 'streaming api', 'pii api', 'self-host mcp'] },
};
